import type { Core, Data } from '@strapi/types';
import { useMutation } from '@tanstack/react-query';

import { useAppContext } from '@/modules/app-context/use-app-context';
import { useApi } from '@/modules/utils/custom-hooks/use-api';
import { createDocumentStatusApi, updateDocumentStatusApi, deleteDocumentStatusApi } from '../api/document-status.api';

export const useDocumentStatusApi = () => {
	const { project } = useAppContext();
	const { onSuccess } = useApi({
		queryKey: ['GET_DOCUMENT_STATUSES'],
		closeModal: false,
	});
	const { onSuccess: onStatusesSuccess } = useApi({
		queryKey: ['GET_PROJECT_DOCUMENTS_LIST', project?.documentId],
		closeModal: false,
	});

	const createDocumentStatus = useMutation({
		mutationKey: ['[POST]:CREATE_DOCUMENT_STATUS'],
		mutationFn: ({
			documentId,
			body,
		}: {
			documentId: Pick<Data.Entity<'api::project.project'>, 'documentId'>['documentId'];
			body: Core.Helpers.Controllers.GetRequestBody<'api::project-status.project-status', 'create'>;
		}) => createDocumentStatusApi({ documentId, body }),
		onSuccess: () => onSuccess('T_SUCCESS_STATUS_CREATED'),
	});

	const updateDocumentStatus = useMutation({
		mutationKey: ['[PUT]:UPDATE_DOCUMENT_STATUS'],
		mutationFn: ({
			documentId,
			body,
		}: {
			documentId: Pick<Data.Entity<'api::project-status.project-status'>, 'documentId'>['documentId'];
			body: Core.Helpers.Controllers.GetRequestBody<'api::project-status.project-status', 'update'>;
		}) => updateDocumentStatusApi({ documentId, body }),
		onSuccess: () => {
			onSuccess();
			onStatusesSuccess();
		},
	});

	const deleteDocumentStatus = useMutation({
		mutationKey: ['[DELETE]:DELETE_DOCUMENT_STATUS'],
		mutationFn: ({ documentId }: { documentId: Pick<Data.Entity<'api::project-status.project-status'>, 'documentId'>['documentId'] }) =>
			deleteDocumentStatusApi({ documentId }),
		onSuccess: () => onSuccess('T_SUCCESS_STATUS_DELETED'),
	});

	return {
		createDocumentStatus,
		deleteDocumentStatus,
		updateDocumentStatus,
	};
};
